import { z } from 'zod'
import { asEnumType } from '@gqloom/zod'

export const DeviceKind = {
  CHECKIN_AGENT: 'CHECKIN_AGENT',
  KIOSK: 'KIOSK',
} as const

export type DeviceKind = (typeof DeviceKind)[keyof typeof DeviceKind]

export const deviceKindSchema = z.enum(DeviceKind).register(asEnumType, {
  name: 'DeviceKind',
  description: 'What a paired device is: a Check-In Agent terminal or a self-service kiosk',
})

export const DeviceCapability = {
  FORMS: 'FORMS',
} as const

export type DeviceCapability = (typeof DeviceCapability)[keyof typeof DeviceCapability]

export const deviceCapabilitySchema = z.enum(DeviceCapability).register(asEnumType, {
  name: 'DeviceCapability',
  description: 'An optional capability staff can grant to a paired kiosk',
})

export const DEVICE_CAPABILITY_SCOPES = {
  FORMS: 'forms:fill',
} as const satisfies Record<DeviceCapability, string>

export const DeviceLiveness = {
  ONLINE: 'ONLINE',
  STALE: 'STALE',
  OFFLINE_NEVER_SEEN: 'OFFLINE_NEVER_SEEN',
} as const

export type DeviceLiveness = (typeof DeviceLiveness)[keyof typeof DeviceLiveness]

export const deviceLivenessSchema = z.enum(DeviceLiveness).register(asEnumType, {
  name: 'DeviceLiveness',
  description: 'Whether a paired device has reported in recently, computed from its last heartbeat',
})

export const DeviceBusyReason = {
  MEMBER_SIGNUP: 'MEMBER_SIGNUP',
  PURCHASE: 'PURCHASE',
  FORM_IN_PROGRESS: 'FORM_IN_PROGRESS',
} as const

export type DeviceBusyReason = (typeof DeviceBusyReason)[keyof typeof DeviceBusyReason]

export const deviceBusyReasonSchema = z.enum(DeviceBusyReason).register(asEnumType, {
  name: 'DeviceBusyReason',
  description: 'What a kiosk reported it is doing when it flagged itself busy',
})

export const KIOSK_HEARTBEAT_INTERVAL_SECONDS = 30
export const CHECKIN_AGENT_HEARTBEAT_INTERVAL_SECONDS = 300
export const KIOSK_STALE_AFTER_SECONDS = 90

export const resolveDeviceKind = (stored: string | null | undefined): DeviceKind => {
  const parsed = deviceKindSchema.safeParse(stored)
  return parsed.success ? parsed.data : DeviceKind.CHECKIN_AGENT
}

export const resolveHeartbeatIntervalSeconds = (kind: DeviceKind): number =>
  kind === DeviceKind.KIOSK
    ? KIOSK_HEARTBEAT_INTERVAL_SECONDS
    : CHECKIN_AGENT_HEARTBEAT_INTERVAL_SECONDS

// Check-In Agents have never had a staleness threshold; a seen Agent stays ONLINE so the
// existing device list reads exactly as before.
export const deriveDeviceLiveness = (
  kind: DeviceKind,
  lastSeenAt: Date | null,
  now: Date
): DeviceLiveness => {
  if (!lastSeenAt) {
    return DeviceLiveness.OFFLINE_NEVER_SEEN
  }
  if (kind === DeviceKind.CHECKIN_AGENT) {
    return DeviceLiveness.ONLINE
  }
  const ageMs = now.getTime() - lastSeenAt.getTime()
  return ageMs > KIOSK_STALE_AFTER_SECONDS * 1000 ? DeviceLiveness.STALE : DeviceLiveness.ONLINE
}

export const deriveDeviceCapabilities = (scope: readonly string[]): DeviceCapability[] =>
  (Object.keys(DEVICE_CAPABILITY_SCOPES) as DeviceCapability[]).filter((capability) =>
    scope.includes(DEVICE_CAPABILITY_SCOPES[capability])
  )
